<template>
  <!-- TODO убрать в рефакторинге скролла и построения страницы -->
  <div class="cards-anchor-wrapper">
    <div
      id="all-cards"
      class="cards-anchor"
      :class="{ 'filters-applied': filterStore.isFiltersApplied || filterStore.searchState.tag__slugs !== 'all' }"
    />
  </div>

  <section :class="cssCardClasses">
    <PausedExperienceMessage
      v-if="isPausedExp"
      :message="pausedMessage"
      :skeleton="isSkeletonActive"
    />

    <div v-if="isShowTitleAndCount">
      <h2
        v-if="isShowTitle"
        class="cards-block__title cards-block__title-tour"
      >
        {{ title }}
      </h2>
      <div class="cards-block__count-sorting">
        <Text
          as="span"
          :text="expCountTitle"
          :skeleton="isSkeletonActive"
          :skeleton-breakpoints="{ default: '144px', tablet: '124px', mobile: '124px' }"
          data-test="exp-count-title"
        />
        <TourFilterSortingSkeleton>
          <TourFilterSorting :default-sogu-fields="defaultSoguFields" />
        </TourFilterSortingSkeleton>
      </div>
    </div>
    <TourCardList
      v-if="isShowCardList"
      :exp-list="expList"
      :context-geo-subject="defaultSoguFields"
      :location="props.location"
      :schema-list-name="schemaListName"
    />
    <UiPaginationV3
      v-if="isPaginationShown"
      :current-page="props.page"
      :is-loading="isFetching"
      :page-count="pagesCount"
      :is-show-more-button-hidden="!hasNextPage"
      @loadMoreClicked="loadMoreClicked"
      @pageSelected="pageSelected"
    />
  </section>
</template>

<script setup lang="ts">
  import { TourFilterSorting, TourFilterSortingSkeleton } from '@/features/filter/sorting';
  import { UiPaginationV3 } from '@experience/vue3-kit';

  import { useGetExperiences } from '@/entities/experience/list';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { choosePlural } from '@experience/shared';
  import { tourFiltersToQuery, useTourFiltersStore } from '@/entities/filter/store/tour';
  import TourCardList from './TourCardList.vue';
  import type { Experience } from '@experience/exp-card/dist/types/experience';

  import { computed, nextTick } from 'vue';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { trackEvent } from '@/entities/sogu';
  import { PausedExperienceMessage } from '@/entities/filter/small-output';
  import { useTourSeoInfo } from '@/entities/region/api/useTourSeoInfo.ts';
  import { useTourCitySeoInfo } from '@/entities/city/api/useTourCitySeoInfo.ts';
  import { isValidMovementType, type TMovementType, useMovementTypes } from '@/entities/movement-type';
  import { Text } from '@/shared/ui/text';
  import { useLoaderStore } from '@/shared/store/useLoaderStore.ts';
  import { type FromCityPayload, LocationType } from '@/entities/location';
  import { useToursCardsSelection } from '@/widgets/expSelectionsBlock/composables/tour/useToursCardsSelection';
  import { ListSortItemValue } from '@/features/filter/sorting/const/config.ts';
  import { useWidgetsShareStore } from '@/features/widgets-share';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { useTourInRegionFromCitySeoInfo } from '@/entities/region/api/useTourInRegionFromCitySeoInfo.ts';
  import { useTourInCountryFromCitySeoInfo } from '@/entities/region/api/useTourInCountryFromCitySeoInfo.ts';
  import { useRegionTagSeoInfo } from '@/entities/region/api/useRegionTagSeoInfo.ts';
  import { useCountryTagSeoInfo } from '@/entities/region/api/useCountryTagSeoInfo';
  import { useTourCityTagSeoInfo } from '@/entities/city/api/useTourCityTagSeoInfo.ts';
  import type { CityInfo, CountryInfo, RegionInfo } from '@/shared/api/experience';
  import { useIsRussianTourLocation } from '@/entities/city';
  import { SKELETON_MIN_VISIBLE_DURATION } from '@/shared/consts';

  const DEFAULT_LIMIT = 54;

  interface Props {
    location: LocationType;
    slug: string;
    page: number;
    defaultSoguFields: ContextGeoSubject;
    geoObject: CityInfo | CountryInfo | RegionInfo;
    fromCitySeoPayload?: Nullable<FromCityPayload>;
  }
  const props = defineProps<Props>();

  const emits = defineEmits<{
    'page-selected': [newPage: number, isScroll?: boolean];
    'excluded-ids-updated': [ids: number[]];
  }>();

  const filterStore = useTourFiltersStore();
  const loaderStore = useLoaderStore();
  const widgetsShareStore = useWidgetsShareStore();
  const seoStore = useSeoStore();

  const currentPage = ref(props.page);
  const experiences = ref<Array<Experience>>([]);
  const hasNextPage = ref(true);
  const isLoadMore = ref(false);

  // Была ли нажата кнопка переключения страниц (для корректного отображения фактоидов при нажатии на loadMore)
  const isPressedPagination = ref(false);

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const { isRussianLocation } = useIsRussianTourLocation();

  const currentTag = computed(() => filterStore.searchState.tag__slugs || 'all');
  const isTourTagPage = computed(() => currentTag.value !== 'all');

  const { isShowBlock: isShowSelectionBlock } = useToursCardsSelection({
    ...props,
    contextGeoSubject: props.defaultSoguFields,
    isExtraSelection: false,
  });

  const title = computed(() => {
    if (props.location === LocationType.CITY || !isShowSelectionBlock.value) {
      return '';
    }

    return t('allCardsBlock.titleTour');
  });

  const { isSkeletonActive } = useListingSkeleton();

  const { tourSeoData: regionSeoData } = useTourSeoInfo(
    computed(() => 'region_tours'),
    computed(() => props.slug),
    computed(() => props.location === LocationType.REGION),
  );

  const { tourSeoData: countrySeoData } = useTourSeoInfo(
    computed(() => 'country_tours'),
    computed(() => props.slug),
    computed(() => props.location === LocationType.COUNTRY),
  );

  const { tourCitySeoData } = useTourCitySeoInfo(
    computed(() => props.slug),
    computed(() => props.location === LocationType.CITY),
  );

  const { cityTagSeoData: tourCityTagSeoData } = useTourCityTagSeoInfo(
    computed(() => props.slug),
    currentTag,
    computed(() => props.location === LocationType.CITY && isTourTagPage.value),
  );

  const { regionTagSeoData } = useRegionTagSeoInfo(
    computed(() => `${props.slug}__${currentTag.value}`),
    computed(() => props.location === LocationType.REGION && isTourTagPage.value && !props.fromCitySeoPayload),
  );

  const { countryTagSeoData } = useCountryTagSeoInfo(
    computed(() => `${props.slug}__${currentTag.value}`),
    computed(() => props.location === LocationType.COUNTRY && isTourTagPage.value && !props.fromCitySeoPayload),
  );

  const { tourInRegionFromCitySeoData } = useTourInRegionFromCitySeoInfo(
    computed(() => props.fromCitySeoPayload?.regionSlug),
    computed(() => props.fromCitySeoPayload?.citySlug),
    computed(() => !!props.fromCitySeoPayload && props.location === LocationType.REGION),
  );

  const { tourInCountryFromCitySeoData } = useTourInCountryFromCitySeoInfo(
    computed(() => props.fromCitySeoPayload?.countrySlug),
    computed(() => props.fromCitySeoPayload?.citySlug),
    computed(() => !!props.fromCitySeoPayload && props.location === LocationType.COUNTRY),
  );

  const { movementTypesExpCardLabels } = useMovementTypes();

  const pausedMessage = computed(() => {
    if (props.location === LocationType.REGION) {
      return (
        (!!props.fromCitySeoPayload ? tourInRegionFromCitySeoData.value?.seo?.h1 : regionSeoData.value?.seo?.h1) || ''
      );
    }

    if (props.location === LocationType.COUNTRY) {
      return (
        (!!props.fromCitySeoPayload ? tourInCountryFromCitySeoData.value?.seo?.h1 : countrySeoData.value?.seo?.h1) || ''
      );
    }

    if (props.location === LocationType.CITY) {
      return tourCitySeoData.value?.seo?.h1 || '';
    }

    return '';
  });

  const schemaListName = computed(() => {
    if (props.location === LocationType.REGION) {
      if (props.fromCitySeoPayload) {
        return tourInRegionFromCitySeoData.value?.seo?.h1 || '';
      }

      return (isTourTagPage.value ? regionTagSeoData.value?.seo?.h1 : regionSeoData.value?.seo?.h1) || '';
    }

    if (props.location === LocationType.COUNTRY) {
      if (props.fromCitySeoPayload) {
        return tourInCountryFromCitySeoData.value?.seo?.h1 || '';
      }

      return (isTourTagPage.value ? countryTagSeoData.value?.seo?.h1 : countrySeoData.value?.seo?.h1) || '';
    }

    if (props.location === LocationType.CITY) {
      return (isTourTagPage.value ? tourCityTagSeoData.value?.seo?.h1 : tourCitySeoData.value?.seo?.h1) || '';
    }

    return '';
  });

  const isShowTitle = computed(() => {
    return (
      (!filterStore.state.tag__slugs || filterStore.state.tag__slugs === 'all') &&
      !filterStore.isFiltersApplied &&
      props.page === 1 &&
      !isSkeletonActive.value &&
      title.value
    );
  });

  const isPaginationShown = computed(() => pagesCount.value > 1 && expList.value.length);

  const limit = computed(() => DEFAULT_LIMIT);

  const offset = ref((props.page - 1 || 0) * limit.value);

  // Логика, чтобы при возврате назад в браузере пересчитывался offset
  // и запрашивались корретные данные

  const manualOffset = ref(false);
  // При первичной загрузке не сбрасываем page из прямой ссылки на первую
  const isInitialLoad = ref(true);

  watch(
    () => [props.page, limit.value],
    ([newPage, newLimit]) => {
      if (!manualOffset.value) {
        offset.value = (newPage - 1 || 0) * newLimit;
        currentPage.value = newPage;
      } else {
        manualOffset.value = false; // сбрасываем флаг после ручного изменения
      }
    },
  );

  const requestParams = computed(() => {
    const isBookable =
      filterStore.isFiltersApplied || filterStore.searchState.sorting !== ListSortItemValue.RANK ? 'true' : 'all';
    const queryParams = tourFiltersToQuery(filterStore.searchState, filterStore.pageParams);

    return {
      ...queryParams,
      limit: limit.value,
      date_filter_mode: queryParams?.start_date || queryParams?.end_date ? 'event_date_range' : undefined,
      offset: offset.value,
      page: currentPage.value,
      bookable: isBookable,
      price_format: 'travelers' as const,
    };
  });

  const { experiencesData, isFetching } = useGetExperiences(requestParams);

  const needShowSkeleton = ref(false);
  const tagChangeStartTime = ref<number | null>(null);

  const applyExpListReady = () => {
    loaderStore.ready.expList = true;
    filterStore.isFirstExperiencesLoaded = true;
    needShowSkeleton.value = false; // сбрасываем
    tagChangeStartTime.value = null;
  };

  const setExpListReady = () => {
    if (tagChangeStartTime.value === null) {
      applyExpListReady();

      return;
    }

    const elapsed = Date.now() - tagChangeStartTime.value;
    const remaining = Math.max(0, SKELETON_MIN_VISIBLE_DURATION - elapsed);

    if (remaining > 0) {
      setTimeout(applyExpListReady, remaining);
    } else {
      applyExpListReady();
    }
  };

  onMounted(() => {
    needShowSkeleton.value = true;
    loaderStore.ready.expList = false;
    filterStore.isFirstExperiencesLoaded = false;
  });

  watch(
    () => props.slug,
    (newSlug, oldSlug) => {
      if (newSlug !== oldSlug) {
        needShowSkeleton.value = true;
        loaderStore.ready.expList = false;
        filterStore.isFirstExperiencesLoaded = false;
        manualOffset.value = true;
        offset.value = 0;
        currentPage.value = 1;
      }
    },
  );

  watch(
    () => isFetching.value,
    (fetching) => {
      if (fetching || !needShowSkeleton.value) {
        return;
      }

      setExpListReady();
    },
  );

  watch(
    () => experiencesData.value?.results,
    (newResults) => {
      if (needShowSkeleton.value && newResults && newResults.length) {
        emits(
          'excluded-ids-updated',
          newResults.map((exp) => exp.id),
        );
      }
    },
    { immediate: true },
  );

  const expCountTitle = computed(() => {
    if (!filterStore.searchTotalCount) {
      return t('allCardsBlock.notSuggestion');
    }

    if (isRussianLocation.value) {
      return `${filterStore.searchTotalCount} ${choosePlural(filterStore.searchTotalCount || 0, [
        t('allCardsBlock.countToursAndExcursionsFirst'),
        t('allCardsBlock.countToursAndExcursionsSecond'),
        t('allCardsBlock.countToursAndExcursionsThird'),
      ])}`;
    }

    return `${filterStore.searchTotalCount} ${choosePlural(filterStore.searchTotalCount || 0, [
      t('allCardsBlock.countSuggestionFirst'),
      t('allCardsBlock.countSuggestionSecond'),
      t('allCardsBlock.countSuggestionThird'),
    ])}`;
  });

  const pagesCount = computed(() => {
    return experiencesData.value?.count ? Math.ceil(experiencesData.value?.count / DEFAULT_LIMIT) : 0;
  });

  const setPhotosForSeo = (data: Experience[]) => {
    const firstPhoto = data?.[0]?.photos?.[0]?.medium;
    const secondPhoto = data?.[1]?.photos?.[0]?.medium;
    const thirdPhoto = data?.[2]?.photos?.[0]?.medium;

    if (firstPhoto) {
      seoStore.setResizeImage(firstPhoto);
    }

    if (secondPhoto) {
      seoStore.setSecondExpImage(secondPhoto);
    }

    if (thirdPhoto) {
      seoStore.setThirdExpImage(thirdPhoto);
    }
  };

  const setRatingForSeo = (data: Experience[]) => {
    const ratingData = data.reduce(
      (acc, item) => {
        acc.reviewCount = acc.reviewCount + item.review_count;
        acc.ratingValue = acc.ratingValue + item.rating;

        return acc;
      },
      {
        reviewCount: 0,
        ratingValue: 0,
      },
    );

    ratingData.ratingValue = +(ratingData.ratingValue / data.length)?.toFixed(2);

    seoStore.setReviewCount(ratingData.reviewCount);
    seoStore.setRatingValue(ratingData.ratingValue);
  };

  //TODO: вынести логику пагинации отдельно, подумать как связать с запросом экспериенсов
  watch(
    experiencesData,
    (newData) => {
      if (newData?.results) {
        // Сохраняем каринок для seo
        setPhotosForSeo(newData.results as Experience[]);
        setRatingForSeo(newData.results as Experience[]);
        if (isLoadMore.value) {
          experiences.value = [...experiences.value, ...newData.results] as Experience[];
          isLoadMore.value = false;
        } else {
          experiences.value = [...newData.results] as Experience[];
        }
        hasNextPage.value = !!newData.next;
      }
    },
    { immediate: true },
  );

  const handlePagination = (newPage: number, loadMore = false) => {
    if (currentPage.value !== newPage) {
      currentPage.value = newPage;
      manualOffset.value = true;
      offset.value = (newPage - 1) * limit.value;
      if (!loadMore) {
        emits('page-selected', newPage, true);
      } else {
        emits('page-selected', newPage);
      }
    }
  };

  const pageSelected = (newPage: number) => {
    isPressedPagination.value = true;
    handlePagination(newPage);
  };

  const loadMoreClicked = (newPage: number) => {
    if (!hasNextPage.value) {
      return;
    }
    isLoadMore.value = true;
    trackEvent('listing:experiences:show-more-button.click', { ...props.defaultSoguFields.soguStamp });
    handlePagination(newPage, true);
  };

  const expList = computed<Experience[]>(() =>
    experiences.value.map((experience) => ({
      ...experience,
      movement_type:
        experience.movement_type && isValidMovementType(experience.movement_type)
          ? (movementTypesExpCardLabels.value?.[experience.movement_type as TMovementType] as string)
          : '',
    })),
  );

  // При выборе другого тэга сбрасываем offset и currentPage
  watch(
    () => filterStore.searchState.tag__slugs,
    () => {
      if (isInitialLoad.value) {
        return;
      }
      needShowSkeleton.value = true;
      loaderStore.ready.expList = false;
      tagChangeStartTime.value = Date.now();
      manualOffset.value = true;
      offset.value = 0;
      currentPage.value = 1;

      nextTick(() => {
        if (!isFetching.value && needShowSkeleton.value) {
          setExpListReady();
        }
      });
    },
  );

  const isShowCardList = computed(() => Boolean(expList.value.length || isSkeletonActive.value));

  const isPausedExp = computed(() => {
    const isEveryExpPaused = expList.value?.every((exp) => exp.status !== 'active');

    return (
      isEveryExpPaused &&
      props.page === 1 &&
      !filterStore.isSearchLoading &&
      !loaderStore.isFiltersLoading &&
      !filterStore.isFiltersApplied
    );
  });

  const isShowTitleAndCount = computed(() => Boolean(expList.value.length && !isPausedExp.value));

  watch(
    () => isPausedExp.value,
    () => {
      widgetsShareStore.isAllExperiencesPaused = isPausedExp.value;
    },
  );

  const cssCardClasses = computed(() => [
    'wrap',
    'cards-block',
    'cards-block-tour',
    { 'cards-block--paused': isPausedExp.value },
    { 'skeleton-active': loaderStore.isFiltersLoading },
    { 'cards-block-tour-country': props.location === LocationType.COUNTRY },
    { 'cards-block-tour-with-tag': filterStore.searchState.tag__slugs !== 'all' },
    { 'cards-block--applied-filter': filterStore.isFiltersApplied || filterStore.searchState.tag__slugs !== 'all' },
  ]);

  watch(
    () => currentPage.value,
    (value) => {
      if (value === 1) {
        isPressedPagination.value = false;
      }
    },
  );

  onMounted(() => {
    if (props.page !== 1) {
      isPressedPagination.value = true;
    }
  });

  // Сбрасываем page на первую страницу, если после применения фильтров вернулся пустой массив экспириенсов
  watch(
    () => experiencesData.value?.results,
    (newResults) => {
      // Первая отдача данных по прямой ссылке: page из URL не трогаем, даже если страница пустая
      if (isInitialLoad.value) {
        if (newResults) {
          isInitialLoad.value = false;
        }

        return;
      }
      if (newResults && newResults.length === 0 && filterStore.isFiltersApplied && currentPage.value !== 1) {
        currentPage.value = 1;
        manualOffset.value = true;
        offset.value = 0;
        emits('page-selected', 1, false);
      }
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .cards-block {
    margin-top: 80px;

    &.skeleton-active {
      min-height: 8303px;
    }

    @include adaptivity.tablet {
      margin-top: 56px;
    }
  }

  .cards-block--paused {
    margin-top: 0;
  }

  .cards-block--applied-filter {
    margin-top: 32px;

    @include adaptivity.mobile {
      margin-top: 16px;
    }
  }

  .cards-block__title {
    @include typography.base-900-850-extra;

    @include adaptivity.tablet {
      @include typography.base-800-750-extra;
    }

    @include adaptivity.mobile {
      @include typography.base-700-500-extra;
    }
  }

  .cards-block__title-tour {
    margin-top: 80px;

    @include adaptivity.tablet {
      margin-top: 56px;
    }
  }

  .cards-block__title--standalone {
    margin-bottom: 24px;
  }

  .cards-block__count-sorting {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 0 24px;

    @include typography.base-400-400-medium;

    @include adaptivity.tablet {
      @include typography.base-300-300-medium;
    }
  }

  .ui-pagination {
    display: flex;
    max-width: 368px;
    margin: 40px auto 0;
    text-align: center;

    @media screen and (max-width: 360px) {
      :deep(.ui-pagination__list-container) {
        padding: 0;
      }

      :deep(.ui-pagination__list) {
        column-gap: 0;
      }
    }
  }

  .cards-anchor-wrapper {
    position: relative;

    .cards-anchor {
      position: absolute;
      top: 80px;

      &.filters-applied {
        top: 20px;

        @include adaptivity.mobile {
          top: 4px;
        }
      }

      @include adaptivity.tablet {
        top: 56px;
      }
    }
  }

  .exp-selection-container {
    position: relative;
    grid-column: span 3;
    box-sizing: border-box;
    padding: 48px 0;
    background-color: var(--alias-color-background-accent-orange-lightest);

    &:empty {
      display: none;
    }

    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 100%;
      width: 48px;
      height: 100%;
      background-color: var(--alias-color-background-accent-orange-lightest);
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 100%;
      width: 48px;
      height: 100%;
      background-color: var(--alias-color-background-accent-orange-lightest);
    }

    :deep(.exp-selection-block) {
      padding-top: 0;

      .exp-selection-slider.exp-card-slider__no-padding .wrap {
        padding-right: 0;
        padding-left: 0;
      }
    }

    @include adaptivity.tablet {
      padding: var(--alias-space-32) 0;
    }

    @include adaptivity.mobile {
      padding: var(--alias-space-24) 0;
    }

    @include adaptivity.tablet {
      grid-column: span 2;
    }

    @include adaptivity.mobile {
      grid-column: 1;
    }
  }
</style>
